/*
 * <sht-zip-check> inline local-delivery ZIP eligibility checker.
 * Used by sections/seaweed-two-ways.liquid on the "Shop Delivery" card.
 *
 * It prefers Zapiet's Delivery Validator API when that app block is loaded.
 * The local ZIP list remains a fallback so the section still works before the
 * validator block is enabled.
 *
 * Config via data-* attributes (all set from the section schema in Liquid):
 *   data-zips      "28512, 28557, ..."  fallback in-zone ZIP list
 *   data-shop-url  "/collections/all"   where both results route to
 *   data-in-msg    in-zone message
 *   data-out-msg   out-of-zone message
 */
if (!customElements.get('sht-zip-check')) {
  class ZipCheck extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.querySelector('.js-zip-trigger');
      this.panel = this.querySelector('.js-zip-panel');
      this.form = this.querySelector('.js-zip-form');
      this.input = this.querySelector('.js-zip-input');
      this.result = this.querySelector('.js-zip-result');
      this.shopUrl = this.normalizeShopUrl(this.dataset.shopUrl || '/collections/all');
      this.inMsg = this.dataset.inMsg || "You're in zone. Pick Local delivery at checkout.";
      this.outMsg = this.dataset.outMsg || 'Out of zone. We still ship.';
      this.checkingMsg = this.dataset.checkingMsg || 'Checking delivery eligibility...';
      this.zips = this.parseZips(this.dataset.zips);
      this.isOpen = false;
      this.renderVersion = 0;
      this.checkVersion = 0;
    }

    connectedCallback() {
      if (this.trigger) {
        this.trigger.addEventListener('click', this.toggle.bind(this, undefined));
      }
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    parseZips(raw) {
      const set = new Set();
      (raw || '').split(/[^0-9]+/).forEach((z) => {
        if (/^\d{5}$/.test(z)) set.add(z);
      });
      return set;
    }

    normalizeShopUrl(raw) {
      try {
        const url = new URL(raw, window.location.origin);
        if (url.origin !== window.location.origin) return '/collections/all';
        return url.pathname + url.search + url.hash;
      } catch (err) {
        return '/collections/all';
      }
    }

    hasZapietValidator() {
      return Boolean(
        window.ZapietDeliveryValidator &&
          typeof window.ZapietDeliveryValidator.checkEligibility === 'function'
      );
    }

    toggle(force) {
      this.isOpen = typeof force === 'boolean' ? force : !this.isOpen;
      if (this.panel) this.panel.hidden = !this.isOpen;
      if (this.trigger) this.trigger.setAttribute('aria-expanded', String(this.isOpen));
      this.classList.toggle('is-open', this.isOpen);
      if (this.isOpen && this.input) {
        requestAnimationFrame(() => this.input.focus());
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.toggle(false);
        if (this.trigger) this.trigger.focus();
      }
    }

    onSubmit(e) {
      e.preventDefault();
      const zip = (this.input ? this.input.value : '').replace(/\D/g, '').slice(0, 5);
      const checkVersion = ++this.checkVersion;
      if (zip.length !== 5) {
        this.render('invalid', 'Please enter a 5-digit ZIP code.');
        if (this.input) this.input.focus();
        return;
      }
      if (this.hasZapietValidator()) {
        this.render('checking', this.checkingMsg);
        const renderIfCurrent = (state, message) => {
          if (checkVersion !== this.checkVersion) return;
          this.render(state, message);
        };
        window.ZapietDeliveryValidator.checkEligibility(
          zip,
          () => renderIfCurrent('in', this.inMsg),
          () => renderIfCurrent('out', this.outMsg),
          () => {
            if (checkVersion !== this.checkVersion) return;
            this.renderLocalResult(zip);
          }
        );
        return;
      }

      this.renderLocalResult(zip);
    }

    renderLocalResult(zip) {
      const inZone = this.zips.has(zip);
      this.render(inZone ? 'in' : 'out', inZone ? this.inMsg : this.outMsg);
    }

    render(state, message) {
      if (!this.result) return;
      this.result.className = 'sw-zipcheck__result js-zip-result is-' + state;

      const nodes = [];
      const msg = document.createElement('p');
      msg.className = 'sw-zipcheck__msg';
      msg.textContent = (state === 'in' ? '✓ ' : '') + message;
      nodes.push(msg);

      if (state === 'in' || state === 'out') {
        const cta = document.createElement('a');
        cta.className =
          'sw-btn sw-zipcheck__cta ' + (state === 'in' ? 'sw-btn--accent' : 'sw-btn--ghost');
        cta.href = this.shopUrl;
        cta.textContent = state === 'in' ? 'Shop now' : 'Shop all';
        nodes.push(cta);
      }

      this.result.replaceChildren();
      const renderVersion = ++this.renderVersion;
      requestAnimationFrame(() => {
        if (renderVersion !== this.renderVersion) return;
        this.result.hidden = false;
        this.result.replaceChildren(...nodes);
      });
    }
  }

  customElements.define('sht-zip-check', ZipCheck);
}
